import { pickBy } from 'lodash';
import { Admin, AdminPermission } from 'src/domain/admin';
import { SupportSession } from 'src/domain/support';
import {
    AdminAnalytics,
    AnalyticsFilter,
    AnalyticsPeople,
    AnalyticsActivity,
    CoverageHistory,
} from 'src/domain/analytics';
import {
    Consumer,
    ConsumerSession,
    ConsumerSessionStatus,
} from 'src/domain/consumer';
import { ContactFormPayload } from 'src/domain/contact';
import { Creator } from 'src/domain/creator';
import { NextOfKin } from 'src/domain/next-of-kin';
import {
    LicenceEntity,
    Organization,
    OrganizationDocumentViewCount,
} from 'src/domain/organization';
import {
    InfoScreen,
    PublicInfoScreen,
    UpdateInfoScreenDto,
} from 'src/domain/infoScreen';
import { Media, PersonalMedia } from 'src/domain/personalMedia';
import { Session } from 'src/domain/session';
import {
    IncompleteSession,
    IncompleteSessionPayload,
} from 'src/domain/sessionForm';
import { SASInfo, User } from 'src/domain/user';
import {
    Message,
    CreateMessageDto,
    CreateMessageCommentDto,
    UpdateMessageCommentDto,
    ListMessagesFilters,
    ListMessageCommentsFilters,
    MessageComment,
    MessageViewer,
    OpenConversationAttentionCountFilters,
    OpenConversationAttentionCountResponse,
    OpenConversationAttentionCommentsCountFilters,
    OpenConversationAttentionCommentsCountResponse,
    ResidentUnreadCountFilters,
    ResidentUnreadCountResponse,
    UpdateMessageDto,
    UpdateMessageCommentingDto,
} from 'src/domain/message';
import { PaginatedResponse } from 'src/domain/pagination';
import {
    EventAudienceCounts,
    EventResponseStatus,
    MunduEvent,
} from 'src/domain/event';
import { HTTPService } from './http.service';
import {
    SessionDto,
    NextOfKinDto,
    AdminDto,
    CreatorDto,
    UpdateCreatorDto,
    ConsumerDto,
    UpdateConsumerDto,
    OrganizationDto,
    UpdateOrganizationDocumentsDto,
    UpdateOrganizationDto,
    UpdateOrganizationModuleDto,
    CreateEventDto,
    UpdateEventDto,
    UpdateEventOccurrenceDto,
    ListEventsDto,
    PersonalMediaDto,
    B2CIdentity,
} from './mundu.params';

const getFilterParams = (
    params: Record<string, string | number | undefined>,
) => {
    const _params = pickBy<Record<string, string | number | undefined>>(
        params,
        v => v !== undefined,
    ) as Record<string, string | number>;
    return { params: _params };
};

type UpdateSessionDto = Partial<SessionDto>;
type UpdateNextOfKinDto = Partial<NextOfKinDto>;
type PushTokenType = 'expo' | 'voip' | 'web';

export class MunduClient {
    // Auth
    private api: HTTPService;
    constructor({ httpService }: { httpService: HTTPService }) {
        this.api = httpService;
    }

    getUser = (): Promise<User> => this.api.get('auth/getUser');
    getUsername = (id: string): Promise<string> =>
        this.api.get(`user/name/${id}`);

    getPhoneNumberExists = (phonenumber: string) =>
        this.api.get<boolean>(`auth/checkPhoneNumberExists/${phonenumber}`);

    getEmailExists = (email: string) =>
        this.api.get<boolean>(`auth/checkEmailExists/${email}`);

    getUsernameExists = (username: string) =>
        this.api.get<boolean>(`auth/checkUsernameExists/${username}`);

    getPersonalMediaSas = (consumerId: string) =>
        this.api.get<SASInfo>(`auth/personalMediaSAS`, {
            params: { consumerId },
        });

    getCommonMediaSas = () => this.api.get<SASInfo>(`auth/commonMediaSAS`);
    getOrganizationDocumentsSas = (organizationId?: string) =>
        this.api.get<SASInfo>(`auth/organizationDocumentsSAS`, {
            params: organizationId ? { organizationId } : undefined,
        });

    getMessageMediaSas = () => this.api.get<SASInfo>(`auth/messageMediaSAS`);

    getInfoScreen = () => this.api.get<InfoScreen>('info-screen');
    getInfoScreenSas = () => this.api.get<SASInfo>('info-screen/sas');
    updateInfoScreen = (payload: UpdateInfoScreenDto) =>
        this.api.patch<InfoScreen>('info-screen', { payload });
    regenerateInfoScreenLink = () =>
        this.api.post<InfoScreen>('info-screen/regenerate-link');
    getPublicInfoScreen = (token: string) =>
        this.api.get<PublicInfoScreen>(`info-screen/public/${token}`);

    registerPushToken = (
        token: string,
        type: PushTokenType,
        platform?: 'ios' | 'android' | 'web',
        locale?: 'en' | 'nb',
    ) =>
        this.api.post<void>('notifications/register', {
            payload: {
                token,
                type,
                platform,
                locale,
            },
        });

    unregisterPushToken = (type?: PushTokenType) =>
        this.api.post<void>('notifications/unregister', {
            payload: {
                type,
            },
        });

    listMessageComments = (
        messageId: string,
        filters: ListMessageCommentsFilters = {},
    ) =>
        this.api.get<PaginatedResponse<MessageComment>>(
            `messages/${messageId}/comments`,
            {
                ...getFilterParams({
                    limit: filters.limit,
                    pageIndex: filters.pageIndex,
                    sortOrder: filters.sortOrder,
                }),
                unwrapData: false,
            },
        );

    listMessageViewers = (messageId: string) =>
        this.api.get<MessageViewer[]>(`messages/${messageId}/viewers`);

    createMessageComment = (
        messageId: string,
        payload: CreateMessageCommentDto,
    ) =>
        this.api.post<MessageComment>(`messages/${messageId}/comments`, {
            payload,
        });

    updateMessageComment = (
        messageId: string,
        commentId: string,
        payload: UpdateMessageCommentDto,
    ) =>
        this.api.patch<MessageComment>(
            `messages/${messageId}/comments/${commentId}`,
            {
                payload,
            },
        );

    deleteMessageComment = (messageId: string, commentId: string) =>
        this.api.delete(`messages/${messageId}/comments/${commentId}`, {
            deserialize: res => res.text(),
        });

    updateMessageCommenting = (
        messageId: string,
        payload: UpdateMessageCommentingDto,
    ) =>
        this.api.patch<Message>(`messages/${messageId}/commenting`, {
            payload,
        });

    getAvatarSas = (organizationId?: string) =>
        this.api.get<SASInfo>(`auth/avatarSAS`, {
            params: organizationId ? { organizationId } : undefined,
        });

    getEntityAvatarSas = (consumerId: string) =>
        this.api.get<SASInfo>(`auth/avatarSAS`, {
            params: { consumerId },
        });

    // Admin
    createAdmin = (payload: AdminDto) =>
        this.api.post<Admin>(`admin`, { payload });

    deleteAdmin = (id: string) =>
        this.api.delete(`admin/${id}`, {
            deserialize: res => res.text(),
        });
    listAdmins = () => this.api.get<Admin[]>(`admin`);
    updateAdminPermissions = (
        id: string,
        adminPermissions: AdminPermission[],
    ) =>
        this.api.patch<Admin>(`admin/${id}/permissions`, {
            payload: { adminPermissions },
        });
    createSupportSession = (organizationId: string) =>
        this.api.post<SupportSession>('support/sessions', {
            payload: { organizationId },
        });
    endSupportSession = (token: string) =>
        this.api.post<void>('support/sessions/end', {
            payload: { token },
            deserialize: res => res.text(),
        });

    // Creators
    listCreators = ({
        email,
        organizationId,
        phoneNumber,
        consumerId,
        creatorId,
        limit,
        pageIndex,
    }: {
        email?: string;
        organizationId?: string;
        phoneNumber?: string;
        consumerId?: string;
        creatorId?: string;
        limit?: number;
        pageIndex?: number;
    }) =>
        this.api.get<Creator[]>(
            'creators',
            getFilterParams({
                email,
                organizationId,
                phoneNumber,
                'consumerIds[0]': consumerId,
                creatorId,
                limit,
                pageIndex,
            }),
        );

    /** Login contacts as staff see them, and whether the invitation is still open. */
    getLoginContacts = (
        role: 'creators' | 'nextOfKins',
        id: string,
        organizationId: string,
    ) =>
        this.api.get<{
            pending: boolean;
            contacts: {
                kind: 'email' | 'phone';
                value: string;
                verified: boolean;
            }[];
        }>(`${role}/${id}/loginContacts`, { params: { organizationId } });
    /** Adds or corrects an unconfirmed email or phone. */
    setLoginContact = (
        role: 'creators' | 'nextOfKins',
        id: string,
        kind: 'email' | 'phone',
        value: string,
        organizationId: string,
    ) =>
        this.api.patch(`${role}/${id}/loginContacts`, {
            payload: { kind, value, organizationId },
            deserialize: res => res.text(),
        });
    removeLoginContact = (
        role: 'creators' | 'nextOfKins',
        id: string,
        kind: 'email' | 'phone',
        organizationId: string,
    ) =>
        this.api.delete(`${role}/${id}/loginContacts/${kind}`, {
            params: { organizationId },
            deserialize: res => res.text(),
        });
    resendAccountInvitation = (
        role: 'creators' | 'nextOfKins',
        id: string,
        kind: 'email' | 'phone',
        organizationId: string,
    ) =>
        this.api.patch(`${role}/resendInvite/${id}`, {
            payload: { kind, organizationId },
        });

    createCreator = (payload: CreatorDto) =>
        this.api.post<Creator>(`creators`, { payload });

    getCreator = (id: string) => this.api.get<Creator>(`creators/${id}`);

    updateCreator = (id: string, { profile, ...rest }: UpdateCreatorDto) =>
        this.api.patch<Creator>(`creators/${id}`, {
            payload: { ...rest, ...profile },
        });

    deleteCreator = (id: string) =>
        this.api.delete(`creators/${id}`, {
            deserialize: res => res.text(),
        });

    updateCreatorOrganizationAdmin = (
        id: string,
        organizationId: string,
        enabled: boolean,
    ) =>
        this.api.patch<Creator>(`creators/${id}/organization-admin`, {
            payload: { organizationId, enabled },
        });

    // Consumers
    listConsumers = ({
        organizationId,
        creatorId,
    }: {
        organizationId?: string;
        creatorId?: string;
    }) =>
        this.api.get<Consumer[]>(
            'consumers',
            getFilterParams({
                organizationId,
                creatorIds: creatorId,
            }),
        );

    createConsumer = (payload: ConsumerDto) =>
        this.api.post<Consumer>(`consumers`, { payload });
    getConsumer = (id: string) => this.api.get<Consumer>(`consumers/${id}`);
    getConsumerToken = (id: string) =>
        this.api.get<{ token: string | null }>(`consumers/${id}/token`);

    updateConsumer = (id: string, payload: UpdateConsumerDto) =>
        this.api.patch<Consumer>(`consumers/${id}`, {
            payload: payload,
        });

    deleteConsumer = (id: string) =>
        this.api.delete(`consumers/${id}`, { deserialize: res => res.text() });

    resetConsumerPassword = (id: string) =>
        this.api.patch<{ password: string; status: string; message: 'string' }>(
            `user/resetPassword/${id}`,
        );

    // NextOfKins
    listNextOfKins = ({
        organizationId,
        consumerId,
        firstName,
        lastName,
    }: {
        firstName?: string;
        lastName?: string;
        organizationId?: string;
        consumerId?: string;
    }) =>
        this.api.get<NextOfKin[]>(
            'nextOfKins',
            getFilterParams({
                'consumerIds[0]': consumerId,
                organizationId,
                firstName,
                lastName,
            }),
        );

    createNextofKin = (payload: NextOfKinDto) =>
        this.api.post<NextOfKin>(`nextOfKins`, { payload });

    getNextOfKin = (id: string) => this.api.get<NextOfKin>(`nextOfKins/${id}`);

    updateNextOfKin = (id: string, { profile, ...rest }: UpdateNextOfKinDto) =>
        this.api.patch<NextOfKin>(`nextOfKins/${id}`, {
            payload: { ...rest, ...profile },
        });

    deleteNextOfKin = (id: string) =>
        this.api.delete(`nextOfKins/${id}`, { deserialize: res => res.text() });

    //Organization
    getNumberOfLicences = () => this.api.get<LicenceEntity>('licence/info');
    getOrgName = () => this.api.get<string>('organizationName');
    getOrgDisplayName = () => this.api.get<string>('organizationName/display');
    listOrganizations = () => this.api.get<Organization[]>('organizations');

    recordForegroundActivity = (organizationId: string) =>
        this.api.post<void>('activity/foreground', {
            payload: { organizationId, platform: 'web' },
            deserialize: async () => undefined,
        });
    getAdminAnalytics = (filter: AnalyticsFilter) =>
        this.api.get<AdminAnalytics>('admin/analytics', {
            params: {
                ...(filter.month ? { month: filter.month } : {}),
                ...(filter.type ? { type: filter.type } : {}),
                ...(filter.organizationIds
                    ? { organizationIds: filter.organizationIds }
                    : {}),
                ...(filter.departmentId
                    ? { departmentId: filter.departmentId }
                    : {}),
                ...(filter.departmentOrganizationId
                    ? {
                          departmentOrganizationId:
                              filter.departmentOrganizationId,
                      }
                    : {}),
            },
            unwrapData: false,
        });
    getAdminAnalyticsPeople = (filter: AnalyticsFilter) =>
        this.api.get<AnalyticsPeople>('admin/analytics/people', {
            ...getFilterParams({ ...filter }),
            unwrapData: false,
        });
    getAdminAnalyticsActivity = (filter: AnalyticsFilter) =>
        this.api.get<AnalyticsActivity>('admin/analytics/activity', {
            ...getFilterParams({ ...filter }),
            unwrapData: false,
        });
    getAdminAnalyticsCoverageHistory = (filter: AnalyticsFilter) =>
        this.api.get<CoverageHistory>('admin/analytics/coverage-history', {
            ...getFilterParams({
                type: filter.type,
                organizationIds: filter.organizationIds,
                departmentId: filter.departmentId,
                departmentOrganizationId: filter.departmentOrganizationId,
            }),
            unwrapData: false,
        });
    getOrganizationAnalyticsPeople = (
        organizationId: string,
        filter: AnalyticsFilter,
    ) =>
        this.api.get<AnalyticsPeople>(
            `organizations/${organizationId}/analytics/people`,
            {
                ...getFilterParams({ departmentId: filter.departmentId }),
                unwrapData: false,
            },
        );
    getOrganizationAnalyticsActivity = (
        organizationId: string,
        filter: AnalyticsFilter,
    ) =>
        this.api.get<AnalyticsActivity>(
            `organizations/${organizationId}/analytics/activity`,
            {
                ...getFilterParams({
                    month: filter.month,
                    departmentId: filter.departmentId,
                }),
                unwrapData: false,
            },
        );
    getOrganizationAnalyticsCoverageHistory = (
        organizationId: string,
        filter: AnalyticsFilter,
    ) =>
        this.api.get<CoverageHistory>(
            `organizations/${organizationId}/analytics/coverage-history`,
            {
                ...getFilterParams({ departmentId: filter.departmentId }),
                unwrapData: false,
            },
        );
    createOrganization = (payload: OrganizationDto): Promise<Organization> =>
        this.api.post<Organization>(`organizations`, { payload });
    getOrganization = (id: string) =>
        this.api.get<Organization>(`organizations/${id}`);
    updateOganization = (id: string, payload: UpdateOrganizationDto) =>
        this.api.patch<Organization>(`organizations/${id}`, { payload });
    deleteLegacyAdministrator = (id: string) =>
        this.api.patch<Organization>(
            `organizations/${id}/legacy-administrator`,
        );
    updateOrganizationDocuments = (
        id: string,
        payload: UpdateOrganizationDocumentsDto,
    ) =>
        this.api.patch<Organization>(`organizations/${id}/documents`, {
            payload,
        });
    listOrganizationDocumentViewCounts = (id: string) =>
        this.api.get<OrganizationDocumentViewCount[]>(
            `organizations/${id}/document-view-counts`,
        );
    updateOrganizationModule = (
        id: string,
        payload: UpdateOrganizationModuleDto,
    ) =>
        this.api.patch<Organization>(`organizations/${id}/modules`, {
            payload,
        });
    deleteOrganization = (id: string) =>
        this.api.delete(`organizations/${id}`, {
            deserialize: res => res.text(),
        });

    //Sessions
    listSessions = ({
        author,
        consumerId,
        limit,
        pageIndex,
    }: {
        author?: string;
        consumerId?: string;
        limit?: number;
        pageIndex?: number;
    }) =>
        this.api.get<Session[]>(
            'sessions',
            getFilterParams({
                author,
                consumerId,
                limit,
                pageIndex,
            }),
        );

    createSession = (payload: SessionDto) =>
        this.api.post<Session>('sessions', { payload });
    updateSession = (id: string, payload: UpdateSessionDto) =>
        this.api.patch<Session>(`sessions/${id}`, { payload });
    getSession = (id: string) => this.api.get<Session>(`sessions/${id}`);

    deleteSession = (id: string) =>
        this.api.delete(`sessions/${id}`, {
            deserialize: res => res.text(),
        });

    // IncompleteSessions
    listIncompleteSessions = (
        author?: string,
        recipient?: string,
        limit?: number,
        pageIndex?: number,
    ) =>
        this.api.get<IncompleteSession[]>(
            'inCompleteSessions',
            getFilterParams({
                author,
                recipient,
                limit,
                pageIndex,
            }),
        );

    createIncompleteSession = (payload: IncompleteSessionPayload) =>
        this.api.post<IncompleteSession>('inCompleteSessions', {
            payload,
        });
    getIncompleteSession = (id: string) =>
        this.api.get<IncompleteSession>(`inCompleteSessions/${id}`);
    updateIncompleteSession = (
        id: string,
        payload: Partial<IncompleteSessionPayload>,
    ) =>
        this.api.patch(`inCompleteSessions/${id}`, {
            payload,
        });
    deleteIncompleteSession = (id: string) =>
        this.api.delete(`inCompleteSessions/${id}`, {
            deserialize: res => res.text(),
        });

    // ConsumerSessions
    getConsumerSession = (id: string) =>
        this.api
            .get<ConsumerSession[]>(
                'sessions/consumerSessions',
                getFilterParams({
                    sessionIds: id,
                }),
            )
            .then(res => res[0]);

    listConsumerSessions = () =>
        this.api.get<ConsumerSession[]>('sessions/consumerSessions');

    updateConsumerSession = (id: string, status: ConsumerSessionStatus) =>
        this.api.patch<ConsumerSession>(`sessions/consumerSessions/${id}`, {
            payload: { status },
        });

    // PersonalMedia
    updatePersonalMedia = (payload: PersonalMediaDto) =>
        this.api.post<PersonalMedia>('personalMedias/upload', { payload });

    listPersonalMedia = ({
        consumerId,
        uploaderId,
        limit,
        pageIndex,
    }: {
        consumerId?: string;
        uploaderId?: string;
        limit?: number;
        pageIndex?: number;
    }) =>
        this.api.get<PersonalMedia[]>(
            `personalMedias`,
            getFilterParams({
                consumerId,
                uploaderId,
                limit,
                pageIndex,
            }),
        );

    deletePersonalMedia = (consumerId: string, imageIds: string[]) =>
        this.api.delete(`personalMedias/media/${consumerId}`, {
            payload: { mediaIds: imageIds },
            deserialize: res => res.text(),
        });

    // CommonMedias
    listCommonMedias = (): Promise<Media[]> => this.api.get(`commonMedias`);

    // SessionLogs
    listSessionLogs = () => this.api.get(`sessionLogs`);

    // Contact
    contact = (payload: ContactFormPayload) =>
        this.api.post('contact', {
            payload,
            options: {
                hooks: {
                    beforeRequest: [],
                },
            },
        });

    // Consent
    updateConsent = (id: string, consent: boolean) =>
        this.api.patch<User>(`user/consent/${id}`, { payload: { consent } });
    getUserName = (id: string) => this.api.get(`user/name/${id}`);

    getSignInIdentities = (id: string) =>
        this.api.get<B2CIdentity[]>(`user/signInIdentities/${id}`);

    assignConsumers = (creatorId: string, consumerIds: string[]) =>
        this.api.patch<{ message: string }>(
            `organizations/assignConsumers/${creatorId}`,
            {
                payload: { consumerIds },
            },
        );

    removeConsumers = (creatorId: string, consumerIds: string[]) =>
        this.api.delete<{ consumerIds: string[] }>(
            `organizations/removeConsumers/${creatorId}`,
            {
                payload: { consumerIds },
            },
        );

    assignCreators = (consumerId: string, creatorIds: string[]) =>
        this.api.patch<{ message: string }>(
            `organizations/assignCreators/${consumerId}`,
            {
                payload: { creatorIds },
            },
        );

    removeCreators = (consumerId: string, creatorIds: string[]) =>
        this.api.delete<{ creatorIds: string[] }>(
            `organizations/removeCreators/${consumerId}`,
            {
                payload: { creatorIds },
            },
        );

    listMessages = ({
        organizationId,
        consumerId,
        strict,
        nextOfKinId,
        allowedGroupIds,
        commentsEnabled,
        readStatus,
        sentByMe,
        directToConsumer,
        sendToAll,
        replyStatus,
        month,
        limit,
        pageIndex,
    }: ListMessagesFilters) => {
        return this.api.post<PaginatedResponse<Message>>('messages/search', {
            payload: {
                organizationId,
                consumerId,
                strict,
                nextOfKinId,
                allowedGroupIds,
                commentsEnabled,
                readStatus: readStatus === 'all' ? undefined : readStatus,
                sentByMe,
                directToConsumer,
                sendToAll,
                replyStatus,
                month,
                limit,
                pageIndex,
            },
            unwrapData: false,
        });
    };

    createMessage = (payload: CreateMessageDto) =>
        this.api.post<Message>('messages', { payload });

    updateMessage = (id: string, payload: UpdateMessageDto) =>
        this.api.patch<Message>(`messages/${id}`, { payload });

    markMessageRead = (id: string) =>
        this.api.post(`messages/${id}/read`, { payload: {} });

    getResidentUnreadMessageCounts = ({
        organizationId,
        consumerIds,
    }: ResidentUnreadCountFilters) => {
        const params = new URLSearchParams();
        params.append('organizationId', organizationId);
        consumerIds.forEach(consumerId => {
            params.append('consumerIds', consumerId);
        });

        return this.api.get<ResidentUnreadCountResponse>(
            'messages/resident-unread-counts',
            { params },
        );
    };

    getOpenConversationAttentionCount = ({
        organizationId,
        consumerIds,
    }: OpenConversationAttentionCountFilters) => {
        const params = new URLSearchParams();
        params.append('organizationId', organizationId);
        consumerIds.forEach(consumerId => {
            params.append('consumerIds', consumerId);
        });

        return this.api.get<OpenConversationAttentionCountResponse>(
            'messages/open-conversations-attention-count',
            { params },
        );
    };

    getOpenConversationCommentsAttentionCount = ({
        organizationId,
        consumerIds,
    }: OpenConversationAttentionCommentsCountFilters) => {
        const params = new URLSearchParams();
        params.append('organizationId', organizationId);
        consumerIds.forEach(consumerId => {
            params.append('consumerIds', consumerId);
        });

        return this.api.get<OpenConversationAttentionCommentsCountResponse>(
            'messages/open-conversation-comments-attention-count',
            { params },
        );
    };

    deleteMessage = (id: string) =>
        this.api.delete(`messages/${id}`, {
            deserialize: res => res.text(),
        });

    //Events
    listEvents = ({
        organizationId,
        from,
        to,
        includeInternal,
    }: ListEventsDto) => {
        const params = new URLSearchParams();
        params.append('organizationId', organizationId);

        if (from) {
            params.append('from', from);
        }

        if (to) {
            params.append('to', to);
        }

        if (includeInternal) {
            params.append('includeInternal', 'true');
        }

        return this.api.get<PaginatedResponse<MunduEvent>>('events', {
            params,
            unwrapData: false,
        });
    };

    getEvent = (id: string, occurrenceStart?: string) => {
        const params = new URLSearchParams();
        if (occurrenceStart) {
            params.append('occurrenceStart', occurrenceStart);
        }

        return this.api.get<MunduEvent>(`events/${id}`, { params });
    };

    getEventResponseStatus = (id: string, occurrenceStart?: string) => {
        const params = new URLSearchParams();
        if (occurrenceStart) {
            params.append('occurrenceStart', occurrenceStart);
        }

        return this.api.get<EventResponseStatus>(
            `events/${id}/response-status`,
            { params },
        );
    };

    sendEventResponseReminder = (
        id: string,
        occurrenceStart?: string,
        retryFailedOnly = false,
    ) =>
        this.api.post<{
            eligibleCount: number;
            sent: number;
            failed: number;
            skippedCooldown: number;
        }>(`events/${id}/response-reminders`, {
            payload: { occurrenceStart, retryFailedOnly },
        });

    createEvent = (payload: CreateEventDto) =>
        this.api.post<MunduEvent>('events', { payload });

    previewEventInvitees = (target: {
        organizationId: string;
        sendToAll?: boolean;
        groupIds?: string[];
        consumerIds?: string[];
    }) =>
        this.api.post<Partial<EventAudienceCounts> & { count: number }>(
            'events/invitee-preview',
            {
                payload: target,
            },
        );

    updateEvent = (id: string, payload: UpdateEventDto) =>
        this.api.patch<MunduEvent>(`events/${id}`, { payload });

    updateEventOccurrence = (
        id: string,
        occurrenceStart: string,
        payload: UpdateEventOccurrenceDto,
    ) =>
        this.api.patch<MunduEvent>(
            `events/${id}/occurrences/${encodeURIComponent(occurrenceStart)}`,
            { payload },
        );

    deleteEvent = (id: string) =>
        this.api.delete(`events/${id}`, {
            deserialize: res => res.text(),
        });

    deleteEventOccurrence = (id: string, occurrenceStart: string) =>
        this.api.delete(
            `events/${id}/occurrences/${encodeURIComponent(occurrenceStart)}`,
            { deserialize: res => res.text() },
        );

    cancelEvent = (id: string) =>
        this.api.patch<MunduEvent>(`events/${id}/cancel`);

    cancelEventOccurrence = (id: string, occurrenceStart: string) =>
        this.api.patch<MunduEvent>(
            `events/${id}/occurrences/${encodeURIComponent(
                occurrenceStart,
            )}/cancel`,
        );

    restoreEvent = (id: string) =>
        this.api.patch<MunduEvent>(`events/${id}/restore`);

    restoreEventOccurrence = (id: string, occurrenceStart: string) =>
        this.api.patch<MunduEvent>(
            `events/${id}/occurrences/${encodeURIComponent(
                occurrenceStart,
            )}/restore`,
        );
}
